// Earth's orientation in the scene, shared by the viewer (main.js) and
// rocketry (rocketry/bodies.js) so both put the same meridian under the Sun.
//
// Scene frame. Planet positions are heliocentric J2000 ecliptic (Standish,
// ephemeris.js) mapped to three.js as (x, y, z)_ecl -> (x, z, -y): +X is the
// J2000 vernal equinox, +Y the ecliptic north pole, -Z ecliptic longitude 90.
//
// Pole. The J2000 celestial pole (RA 0h, Dec +90) lies 23.44 deg from the
// ecliptic pole toward ecliptic longitude 90, at scene (0, cos e, -sin e).
// A group with rotation.x = -EARTH_OBLIQUITY carries its +Y there and keeps
// +X on the equinox, so inside it the J2000 equatorial axes map the same way
// the ecliptic ones do: (x, y, z)_eq -> (x, z, -y). The Sun then stands over
// the Tropic of Cancer at the June solstice and over the equator at the
// equinoxes.
//
// Spin. Inside that group a mesh with rotation.y = a has its local +X (texture
// longitude 0, Greenwich) at right ascension a. With a = the Earth rotation
// angle for the date, every meridian is where it is at that instant: the Sun
// stands over longitude -(UT - 12 h) - equation of time.
//
// Precession is left out. The J2000 pole is 0.15 deg from the 2026 pole and
// moves 0.0056 deg a year, below what the globe can show; the season test
// (scripts/test-earth-season.mjs) allows 0.3 deg, which holds until about 2050.
// To carry it later: rotation.set(-epsA, -pA, 0, 'YXZ') with the general
// precession pA = 5028.796" T + 1.105" T^2 and obliquity of date
// epsA = 84381.406" - 46.837" T (T in Julian centuries from J2000), and the
// spin angle becomes GMST in place of the rotation angle.

const TWO_PI = Math.PI * 2;

// Mean obliquity of the ecliptic at J2000 (IAU 1976, 84381.448 arcsec; the
// IAU 2006 value, 84381.406 arcsec, is 0.042 arcsec less, far below anything
// the globe can show).
export const EARTH_OBLIQUITY = 23.4392911 * Math.PI / 180;

// Turns of the Earth per UT1 day relative to the stars (IERS Conventions 2010).
export const EARTH_SIDEREAL_TURNS_PER_DAY = 1.00273781191135448;

// Earth rotation angle in radians (0..2pi) at Julian date `jd`, IERS
// Conventions 2010 eq. 5.15. UTC stands in for UT1 (under a second apart).
// The whole days are dropped before the multiply so the angle keeps full
// precision far from J2000.
export function earthRotationAngle(jd) {
  const d = jd - 2451545.0;
  let turns = (0.779057273264 + (d - Math.floor(d)) + (EARTH_SIDEREAL_TURNS_PER_DAY - 1) * d) % 1;
  if (turns < 0) turns += 1;
  return turns * TWO_PI;
}

// Equation of time in minutes (apparent minus mean solar time) at Julian date
// `jd`, from the NOAA solar calculator (Meeus). Good to a few seconds for
// 1800-2100. Local apparent solar time = UT + longitude / 15 + this / 60.
export function equationOfTimeMinutes(jd) {
  const DEG = Math.PI / 180;
  const T = (jd - 2451545.0) / 36525;
  const L0 = (280.46646 + T * (36000.76983 + T * 0.0003032)) * DEG;
  const M = (357.52911 + T * (35999.05029 - 0.0001537 * T)) * DEG;
  const e = 0.016708634 - T * (0.000042037 + 0.0000001267 * T);
  const omega = (125.04 - 1934.136 * T) * DEG;
  const eps0 = 23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60;
  const eps = (eps0 + 0.00256 * Math.cos(omega)) * DEG;
  const y = Math.tan(eps / 2) ** 2;
  const eq = y * Math.sin(2 * L0) - 2 * e * Math.sin(M)
    + 4 * e * y * Math.sin(M) * Math.cos(2 * L0)
    - 0.5 * y * y * Math.sin(4 * L0) - 1.25 * e * e * Math.sin(2 * M);
  return 4 * eq / DEG;
}
